import React, { useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { MailCheck, ArrowLeft } from "lucide-react";
import { api } from "../lib/api";

export default function ForgotPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch {
      // Backend always returns 200; only show generic error on network failure
      toast.error(t("auth.forgot_error_network"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-[100svh] pt-32 pb-20 bg-cream" data-testid="forgot-password-page">
      <div className="max-w-[1100px] mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
        <div>
          <div className="text-[11px] tracking-[0.3em] uppercase text-slate1">{t("auth.forgot_section")}</div>
          <h1 className="mt-4 font-display text-4xl lg:text-5xl font-light text-ink">{t("auth.forgot_title")}</h1>
          <p className="mt-6 text-slate1 leading-relaxed" dangerouslySetInnerHTML={{ __html: t("auth.forgot_sub") }} />
          <Link to="/login" className="mt-8 inline-flex items-center gap-2 text-[12px] tracking-[0.2em] uppercase text-slate1 hover:text-ink">
            <ArrowLeft size={14} /> {t("auth.back_to_login")}
          </Link>
        </div>

        {sent ? (
          <div className="bg-white border border-ink/10 p-8 md:p-10 rounded-sm" data-testid="forgot-password-sent">
            <MailCheck size={28} className="text-gold" />
            <div className="mt-4 text-[11px] tracking-[0.3em] uppercase text-gold">{t("auth.forgot_sent_header")}</div>
            <h2 className="mt-2 font-display text-3xl text-ink font-light">{t("auth.forgot_sent_title")}</h2>
            <p className="mt-4 text-slate1 leading-relaxed">
              <Trans i18nKey="auth.forgot_sent_body" values={{ email }} />
            </p>
            <p className="mt-3 text-sm text-slate1">{t("auth.forgot_sent_spam")}</p>
            <Link to="/login" className="mt-8 inline-block bg-ink text-cream px-6 py-3 text-[12px] tracking-[0.2em] uppercase rounded-sm hover-lift" data-testid="forgot-password-back-login">
              {t("auth.back_to_login")}
            </Link>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="bg-white border border-ink/10 p-8 md:p-10 rounded-sm" data-testid="forgot-password-form">
            <label className="block text-[11px] tracking-[0.2em] uppercase text-slate1">{t("auth.email")}</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="tu@studio.it"
              className="mt-2 w-full border border-ink/20 px-4 py-3 bg-cream focus:outline-none focus:border-ink rounded-sm"
              data-testid="forgot-password-email"
            />
            <button
              type="submit"
              disabled={loading}
              className="mt-8 w-full bg-ink text-cream py-4 text-[12px] tracking-[0.2em] uppercase rounded-sm hover-lift disabled:opacity-60"
              data-testid="forgot-password-submit"
            >
              {loading ? t("auth.forgot_submit_loading") : t("auth.forgot_submit")}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
